<template>
  <KTCodePreview v-bind:title="'Danh sách phương án xử lý bảo hành'">
    <template v-slot:toolbar>
      <b-row>
        <b-button variant="primary" @click="openCreateModal">
          <i class="fa fa-plus mr-2"></i>
          Thêm mới
        </b-button>
      </b-row>
    </template>

    <template v-slot:preview>
      <b-row>
        <b-col cols="12">
          <b-card class="border">
            <b-card-title>
              <b-row align-v="center">
                <b-col cols="12" md="6" lg="4">
                  <b-input-group>
                    <b-form-input
                      v-model="search"
                      placeholder="Tìm kiếm theo mã hoặc tên"
                      trim
                      @keyup.enter="resetPaginationAndFetch"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        @click="clearSearch"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                      <b-button
                        variant="outline-secondary"
                        @click="resetPaginationAndFetch"
                      >
                        <i class="fa fa-search"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>

                <b-col cols="6" md="3" lg="2" class="mt-3 mt-md-0">
                  <b-form-select
                    v-model="statusFilter"
                    :options="statusOptions"
                    size="sm"
                    @change="resetPaginationAndFetch"
                  ></b-form-select>
                </b-col>
              </b-row>
            </b-card-title>

            <b-card-text>
              <b-table
                :items="solutions"
                :fields="fields"
                :busy="isLoading"
                responsive
                hover
                bordered
                show-empty
                empty-text="Không có dữ liệu"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Đang tải...</strong>
                  </div>
                </template>

                <template v-slot:cell(isActive)="row">
                  <b-badge :variant="row.item.isActive ? 'success' : 'danger'">
                    {{ row.item.isActive ? 'Hoạt động' : 'Không hoạt động' }}
                  </b-badge>
                </template>

                <template v-slot:cell(actions)="row">
                  <b-button-group size="sm">
                    <b-button
                      variant="outline-primary"
                      @click="openEditModal(row.item)"
                      title="Chỉnh sửa"
                    >
                      <i class="fa fa-edit"></i>
                    </b-button>
                    <b-button
                      variant="outline-success"
                      v-if="!row.item.isActive"
                      @click="updateActiveStatus(row.item.id, true)"
                      title="Kích hoạt"
                    >
                      <i class="fa fa-check"></i>
                    </b-button>
                    <b-button
                      variant="outline-warning"
                      v-if="row.item.isActive"
                      @click="updateActiveStatus(row.item.id, false)"
                      title="Vô hiệu hóa"
                    >
                      <i class="fa fa-ban"></i>
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      @click="confirmDelete(row.item)"
                      title="Xóa"
                    >
                      <i class="fa fa-trash"></i>
                    </b-button>
                  </b-button-group>
                </template>
              </b-table>

              <b-row>
                <b-col>
                  <p class="mt-3 text-dark" style="font-weight: 500">
                    Tổng số: {{ totalItems }}
                  </p>
                </b-col>
                <b-col>
                  <b-pagination-nav
                    v-show="totalPages >= 2"
                    class="custom-pagination"
                    :link-gen="linkGen"
                    :number-of-pages="totalPages"
                    use-router
                    align="right"
                    first-class="page-item-first btn btn-icon btn-sm m-1"
                    prev-class="page-item-prev btn btn-icon btn-sm m-1"
                    next-class="page-item-next btn btn-icon btn-sm m-1"
                    last-class="page-item-last btn btn-icon btn-sm m-1"
                    page-class="btn btn-icon btn-sm border-0 m-1"
                  >
                    <template v-slot:first-text>
                      <span><i class="ki ki-bold-double-arrow-back icon-xs"></i></span>
                    </template>
                    <template v-slot:prev-text>
                      <i class="ki ki-bold-arrow-back icon-xs"></i>
                    </template>
                    <template v-slot:next-text>
                      <i class="ki ki-bold-arrow-next icon-xs"></i>
                    </template>
                    <template v-slot:last-text>
                      <span class="text-info">
                        <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                      </span>
                    </template>
                  </b-pagination-nav>
                </b-col>
              </b-row>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>

      <!-- Create/Edit Modal -->
      <solution-form-modal
        :show="showFormModal"
        :solution="selectedSolution"
        :is-edit="isEdit"
        @close="closeFormModal"
        @solution-saved="handleSolutionSaved"
      />

      <!-- Delete confirmation dialog -->
      <b-modal
        v-model="showDeleteModal"
        title="Xác nhận xóa"
        header-bg-variant="light"
        ok-variant="danger"
        ok-title="Xóa"
        cancel-title="Hủy bỏ"
        @ok="deleteSolution"
        :ok-disabled="isDeleting"
      >
        <p>
          Bạn có chắc chắn muốn xóa phương án xử lý
          <strong>{{ selectedSolution?.name || '' }}</strong
          >?
        </p>
        <template #modal-footer="{ ok, cancel }">
          <b-button variant="secondary" @click="cancel()">
            Hủy bỏ
          </b-button>
          <b-button variant="danger" @click="ok()" :disabled="isDeleting">
            <b-spinner small v-if="isDeleting" class="mr-1"></b-spinner>
            Xóa
          </b-button>
        </template>
      </b-modal>
    </template>
  </KTCodePreview>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import SolutionFormModal from './SolutionFormModal.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { SolutionService } from './service/solution.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'SolutionList',
  components: {
    KTCodePreview,
    SolutionFormModal,
  },
  data() {
    return {
      fields: [
        { key: 'code', label: 'Mã', sortable: true },
        { key: 'name', label: 'Tên', sortable: true },
        { key: 'isActive', label: 'Trạng thái' },
        { key: 'actions', label: 'Thao tác', class: 'text-center' },
      ],
      solutions: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      limit: 10,
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      search: '',
      statusFilter: null,
      statusOptions: [
        { text: 'Tất cả trạng thái', value: null },
        { text: 'Hoạt động', value: true },
        { text: 'Không hoạt động', value: false },
      ],
      isLoading: false,
      showFormModal: false,
      isEdit: false,
      selectedSolution: null,
      showDeleteModal: false,
      isDeleting: false,
    };
  },
  watch: {
    '$route.query.page'() {
      this.fetchSolutions();
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Danh sách phương án xử lý bảo hành' },
    ]);
    this.fetchSolutions();
  },
  methods: {
    fetchSolutions() {
      this.isLoading = true;

      const page = parseInt(this.$route.query.page, 10) || 1;

      const params = {
        page,
        limit: this.limit,
      };

      if (this.search) {
        params.search = this.search;
      }

      if (this.statusFilter !== null) {
        params.isActive = this.statusFilter;
      }

      SolutionService.getAll(params)
        .then((response) => {
          if (response.data.status === 200) {
            const responseData = response.data.data;
            this.solutions = responseData.items;
            this.totalItems = responseData.totalItems;
            this.totalPages = responseData.totalPages;
            this.currentPage = responseData.currentPage;
          } else {
            makeToastFaile(response.data.message || 'Lỗi khi tải dữ liệu');
          }
        })
        .catch((error) => {
          console.error('Error fetching solutions:', error);
          makeToastFaile(error.message || 'Lỗi khi tải dữ liệu');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    clearSearch() {
      this.search = '';
      this.resetPaginationAndFetch();
    },

    resetPaginationAndFetch() {
      const query = { ...this.$route.query };
      delete query.page;
      this.$router
        .replace({ query })
        .then(() => {
          this.fetchSolutions();
        })
        .catch(() => {
          this.fetchSolutions();
        });
    },

    openCreateModal() {
      this.isEdit = false;
      this.selectedSolution = {
        code: '',
        name: '',
        isActive: true,
      };
      this.showFormModal = true;
      console.log('Opening create modal:', this.showFormModal);
    },

    openEditModal(solution) {
      this.isEdit = true;
      this.selectedSolution = { ...solution };
      this.showFormModal = true;
      console.log('Opening edit modal:', this.showFormModal);
    },

    closeFormModal() {
      this.showFormModal = false;
      this.selectedSolution = null;
    },

    handleSolutionSaved() {
      this.closeFormModal();
      this.fetchSolutions();
    },

    confirmDelete(solution) {
      this.selectedSolution = solution;
      this.showDeleteModal = true;
    },

    deleteSolution() {
      if (!this.selectedSolution) return;

      this.isDeleting = true;

      SolutionService.delete(this.selectedSolution.id)
        .then((response) => {
          if (response.data.status === 200) {
            makeToastSuccess('Xóa phương án xử lý thành công!');
            this.fetchSolutions();
          } else {
            makeToastFaile(
              response.data.message || 'Lỗi khi xóa phương án xử lý',
            );
          }
        })
        .catch((error) => {
          console.error('Error deleting solution:', error);
          makeToastFaile(error.message || 'Lỗi khi xóa phương án xử lý');
        })
        .finally(() => {
          this.isDeleting = false;
          this.showDeleteModal = false;
          this.selectedSolution = null;
        });
    },

    updateActiveStatus(id, newStatus) {
      SolutionService.updateActiveStatus(id, newStatus)
        .then((response) => {
          if (response.data.status === 200) {
            const statusText = newStatus ? 'kích hoạt' : 'vô hiệu hóa';
            makeToastSuccess(`Đã ${statusText} phương án xử lý thành công!`);
            this.fetchSolutions();
          } else {
            makeToastFaile(
              response.data.message || 'Lỗi khi cập nhật trạng thái',
            );
          }
        })
        .catch((error) => {
          console.error('Error updating status:', error);
          makeToastFaile(error.message || 'Lỗi khi cập nhật trạng thái');
        });
    },
  },
};
</script>

<style scoped>
.card-title {
  margin-bottom: 1rem;
}

.border-table {
  border: 1px solid #dee2e6;
}

.border-table >>> th,
.border-table >>> td {
  border: 1px solid #dee2e6;
}
</style>
